<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <AppSidebar activeItem="knowledge" />

            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="ckb-wrapper">

                    <!-- Header -->
                    <div class="ckb-header">
                        <div class="ckb-header-left">
                            <div class="ckb-header-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="ckb-title">Company Knowledge Base</h1>
                                <p class="ckb-subtitle">Maintain your company's profile, financials, experience and
                                    compliance records used to qualify for bids.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Tabs -->
                    <div class="ckb-tabs">
                        <button v-for="tab in tabs" :key="tab.key"
                            :class="['ckb-tab', { active: activeTab === tab.key }]" @click="activeTab = tab.key">
                            <span v-html="tab.icon"></span>
                            {{ tab.label }}
                        </button>
                    </div>

                    <!-- ============ COMPANY PROFILE (singleton) ============ -->
                    <div v-if="activeTab === 'profile'" class="ckb-panel">
                        <div class="panel-head">
                            <h2>Company Profile</h2>
                            <button v-if="isAdmin" class="ckb-btn primary" @click="openProfileForm">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.5">
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                                </svg>
                                {{ profile ? 'Edit' : 'Add' }}
                            </button>
                        </div>

                        <div v-if="loading" class="ckb-state"><div class="ckb-spinner"></div>Loading…</div>
                        <div v-else-if="!profile" class="ckb-state empty">No company profile yet. Click “Add” to create
                            one.</div>
                        <div v-else class="profile-view">
                            <div v-for="f in profileFields" :key="f.key" class="profile-field">
                                <span class="pf-label">{{ f.label }}</span>
                                <span class="pf-value">{{ displayValue(profile[f.key], f) }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- ============ LIST SECTIONS ============ -->
                    <div v-else class="ckb-panel">
                        <div class="panel-head">
                            <h2>{{ currentConfig.label }}</h2>
                            <button v-if="isAdmin" class="ckb-btn primary" @click="openCreate">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.5">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                Add {{ currentConfig.singular }}
                            </button>
                        </div>

                        <div v-if="loading" class="ckb-state"><div class="ckb-spinner"></div>Loading…</div>
                        <div v-else-if="!rows.length" class="ckb-state empty">No {{ currentConfig.label.toLowerCase() }}
                            records yet.</div>
                        <div v-else class="ckb-table-wrap">
                            <table class="ckb-table">
                                <thead>
                                    <tr>
                                        <th v-for="col in currentConfig.columns" :key="col.key">{{ col.label }}</th>
                                        <th class="col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in rows" :key="row.id">
                                        <td v-for="col in currentConfig.columns" :key="col.key">
                                            <a v-if="col.type === 'file' && row[col.key]" href="#"
                                                @click.prevent="downloadFile(row.id, col.download)">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2"
                                                    style="vertical-align:-2px;margin-right:3px;">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                                Download
                                            </a>
                                            <span v-else>{{ displayValue(row[col.key], col) }}</span>
                                        </td>
                                        <td class="col-actions">
                                            <button class="icon-btn view" title="View" aria-label="View" @click="openView(row)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            </button>
                                            <button v-if="isAdmin" class="icon-btn edit" title="Edit" aria-label="Edit"
                                                @click="openEdit(row)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <path d="M12 20h9" />
                                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                                                </svg>
                                            </button>
                                            <button v-if="isAdmin" class="icon-btn delete" title="Delete" aria-label="Delete"
                                                @click="confirmDelete(row)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <!-- ============ MODAL (Add / Edit / View / Profile) ============ -->
        <div v-if="modal.open" class="ckb-modal-overlay" @click.self="closeModal">
            <div class="ckb-modal">
                <div class="ckb-modal-head">
                    <h3>{{ modal.title }}</h3>
                    <button class="ckb-modal-close" @click="closeModal" aria-label="Close">&times;</button>
                </div>

                <div class="ckb-modal-body">
                    <div v-for="f in modal.fields" :key="f.key" class="ckb-form-row">
                        <label>{{ f.label }}<span v-if="f.required" class="req">*</span></label>

                        <!-- View mode -->
                        <template v-if="modal.mode === 'view'">
                            <div v-if="f.type === 'file'" class="form-static">
                                <a v-if="form[f.key]" href="#" @click.prevent="downloadFile(modal.rowId, f.download)">Download
                                    file</a>
                                <span v-else>—</span>
                            </div>
                            <div v-else class="form-static">{{ displayValue(form[f.key], f) || '—' }}</div>
                            <div v-if="f.key === 'work_start_date' && form.total_experience_years != null"
                                class="form-hint">Total experience: {{ form.total_experience_years }} years</div>
                        </template>

                        <!-- Edit / Create mode -->
                        <div v-else class="ckb-control">
                            <textarea v-if="f.type === 'textarea'" v-model="form[f.key]" rows="3" class="ckb-input"
                                :class="{ 'has-error': errors[f.key] }"
                                @input="clearError(f.key)"></textarea>
                            <select v-else-if="f.type === 'select'" v-model="form[f.key]" class="ckb-input"
                                :class="{ 'has-error': errors[f.key] }" @change="clearError(f.key)">
                                <option value="">Select…</option>
                                <option v-for="opt in f.options" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                            <template v-else-if="f.type === 'file'">
                                <input type="file" class="ckb-input" accept=".pdf,.jpg,.jpeg"
                                    :class="{ 'has-error': errors[f.key] }"
                                    @change="onFileChange($event, f.key)" />
                                <div class="form-hint">Only PDF or JPG files are allowed.</div>
                                <div v-if="form[f.key]" class="form-hint">Current file kept unless you choose a new one.
                                </div>
                            </template>
                            <input v-else :type="f.type || 'text'" v-model="form[f.key]" class="ckb-input"
                                :class="{ 'has-error': errors[f.key] }" @input="clearError(f.key)" />

                            <div v-if="f.key === 'work_start_date'" class="form-hint">Used to auto-calculate total
                                experience.</div>

                            <!-- Inline validation error -->
                            <div v-if="errors[f.key]" class="field-error">{{ errors[f.key] }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="modal.mode !== 'view'" class="ckb-modal-foot">
                    <button class="ckb-btn ghost" @click="closeModal">Cancel</button>
                    <button class="ckb-btn primary" :disabled="saving" @click="save">
                        {{ saving ? 'Saving…' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import { notify } from '@/utils/notify';
import { confirmAction } from '@/utils/confirm';

// Field config shared between table columns, forms and detail views.
const FIN_YEARS = ['2020-21', '2021-22', '2022-23', '2023-24', '2024-25', '2025-26', '2026-27'];

export default {
    name: 'CompanyKnowledgeBase',
    components: { AppSidebar },
    props: { getAccessToken: Function },
    data() {
        return {
            activeTab: 'profile',
            loading: false,
            saving: false,
            profile: null,
            rows: [],
            form: {},
            uploads: {},          // key -> File object for this submission
            errors: {},           // key -> inline validation message
            modal: { open: false, mode: 'create', title: '', fields: [], rowId: null },

            tabs: [
                { key: 'profile', label: 'Company Profile', icon: this.icon('M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z') },
                { key: 'financial', label: 'Financial Health', icon: this.icon('M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6') },
                { key: 'work', label: 'Work Experience', icon: this.icon('M20 7h-9M14 17H5M17 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM7 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z') },
                { key: 'software', label: 'Software', icon: this.icon('M2 3h20v14H2zM8 21h8M12 17v4') },
                { key: 'manpower', label: 'Manpower & Team', icon: this.icon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z') },
                { key: 'audit', label: 'Audit Details', icon: this.icon('M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11') },
                { key: 'signatory', label: 'Authorised Signatories', icon: this.icon('M20 6L9 17l-5-5M16 3l5 5') },
                { key: 'contact', label: 'Contact Persons', icon: this.icon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z') },
                { key: 'banker', label: 'Company Bankers', icon: this.icon('M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3') },
            ],

            profileFields: [
                { key: 'company_name', label: 'Company Name' },
                { key: 'pan_no', label: 'PAN No' },
                { key: 'gst_no', label: 'GST No' },
                { key: 'address', label: 'Address', type: 'textarea' },
                { key: 'company_start_date', label: 'Company Start Date', type: 'date', fmt: 'date' },
                { key: 'net_worth', label: 'Net Worth', fmt: 'money' },
                { key: 'total_assets', label: 'Total Assets', fmt: 'money' },
                { key: 'total_liabilities', label: 'Total Liabilities', fmt: 'money' },
                { key: 'max_capability_amount', label: 'Max Capability Amount (Rs.)', fmt: 'money' },
                // Registration / incorporation details (asked for in bidder forms)
                { key: 'cin', label: 'CIN (Corporate Identity No.)' },
                { key: 'company_type', label: 'Company Type' },
                { key: 'country_of_incorporation', label: 'Country of Incorporation' },
                { key: 'date_of_incorporation', label: 'Date of Incorporation', type: 'date', fmt: 'date' },
                { key: 'registered_office_address', label: 'Registered Office Address', type: 'textarea' },
            ],

            configs: {
                financial: {
                    label: 'Financial Health', singular: 'Year', endpoint: 'financial', multipart: false,
                    columns: [
                        { key: 'finance_year', label: 'Finance Year' },
                        { key: 'total_turnover', label: 'Total Turnover / Gross Revenue', fmt: 'money' },
                        { key: 'net_profit_after_tax', label: 'Net Profit After Tax', fmt: 'money' },
                    ],
                    fields: [
                        { key: 'finance_year', label: 'Finance Year', type: 'select', options: FIN_YEARS, required: true },
                        { key: 'total_turnover', label: 'Total Turnover / Gross Revenue', type: 'number' },
                        { key: 'net_profit_after_tax', label: 'Net Profit After Tax', type: 'number' },
                    ],
                },
                work: {
                    label: 'Work Experience', singular: 'Experience', endpoint: 'work-experience', multipart: true,
                    columns: [
                        { key: 'client_name', label: 'Client' },
                        { key: 'project_name', label: 'Project' },
                        { key: 'executed_work_value', label: 'Value (Rs.)', fmt: 'money' },
                        { key: 'date_of_completion', label: 'Completion', fmt: 'date' },
                        { key: 'certificate_file', label: 'Certificate', type: 'file', download: 'certificate' },
                    ],
                    fields: [
                        { key: 'client_name', label: 'Client Name', required: true },
                        { key: 'project_name', label: 'Name of the Project', required: true },
                        { key: 'work_order_reference', label: 'Work Order Reference' },
                        { key: 'completion_certificate_reference', label: 'Completion Certificate Reference' },
                        { key: 'executed_work_value', label: 'Value of Executed Work (Rs.)', type: 'number' },
                        { key: 'work_description', label: 'Work Descriptions', type: 'textarea' },
                        { key: 'date_of_completion', label: 'Date of Completion', type: 'date' },
                        { key: 'certificate_file', label: 'Upload Certificate', type: 'file', upload: 'certificate', download: 'certificate' },
                    ],
                },
                software: {
                    label: 'Software Management', singular: 'Software', endpoint: 'software', multipart: true,
                    columns: [
                        { key: 'software_name', label: 'Software' },
                        { key: 'license_type', label: 'License Type' },
                        { key: 'number_of_licenses', label: 'Licenses' },
                        { key: 'expiry_date', label: 'Expiry', fmt: 'date' },
                        { key: 'invoice_file', label: 'Invoice', type: 'file', download: 'invoice' },
                    ],
                    fields: [
                        { key: 'software_name', label: 'Software Name', required: true },
                        { key: 'license_type', label: 'License Type' },
                        { key: 'number_of_licenses', label: 'Number of Licenses', type: 'number' },
                        { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
                        { key: 'invoice_file', label: 'Upload Invoice', type: 'file', upload: 'invoice', download: 'invoice' },
                    ],
                },
                manpower: {
                    label: 'Manpower & Team', singular: 'Member', endpoint: 'manpower', multipart: true,
                    columns: [
                        { key: 'employee_name', label: 'Employee' },
                        { key: 'date_of_birth', label: 'Date of Birth', fmt: 'date' },
                        { key: 'work_start_date', label: 'Work Start', fmt: 'date' },
                        { key: 'total_experience_years', label: 'Experience (yrs)' },
                        { key: 'area_of_expertise', label: 'Area of Expertise' },
                        { key: 'cv_file', label: 'CV', type: 'file', download: 'cv' },
                    ],
                    fields: [
                        { key: 'employee_name', label: 'Employee Name', required: true },
                        { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                        { key: 'work_start_date', label: 'Professional Work Start Date', type: 'date' },
                        { key: 'area_of_expertise', label: 'Area of Expertise' },
                        { key: 'cv_file', label: 'Upload CV / Resume', type: 'file', upload: 'cv', download: 'cv' },
                    ],
                },
                audit: {
                    label: 'Audit Details', singular: 'Audit', endpoint: 'audit', multipart: true,
                    columns: [
                        { key: 'audit_number', label: 'Audit No' },
                        { key: 'date_of_audit', label: 'Date of Audit', fmt: 'date' },
                        { key: 'financial_year', label: 'Financial Year' },
                        { key: 'ca_name', label: 'CA Name' },
                        { key: 'audit_report_file', label: 'Report', type: 'file', download: 'report' },
                    ],
                    fields: [
                        { key: 'audit_number', label: 'Audit Number', required: true },
                        { key: 'date_of_audit', label: 'Date of Audit', type: 'date' },
                        { key: 'financial_year', label: 'Financial Year', type: 'select', options: FIN_YEARS },
                        { key: 'ca_name', label: 'CA (Auditor) Name' },
                        { key: 'ca_registration_number', label: 'CA Registration Number' },
                        { key: 'audit_report_file', label: 'Upload Audit Report', type: 'file', upload: 'audit_report', download: 'report' },
                    ],
                },
                signatory: {
                    label: 'Authorised Signatories', singular: 'Signatory', endpoint: 'signatory', multipart: false,
                    columns: [
                        { key: 'name', label: 'Name' },
                        { key: 'designation', label: 'Designation' },
                        { key: 'dsc_id', label: 'DSC ID' },
                        { key: 'phone', label: 'Phone' },
                        { key: 'email', label: 'Email' },
                    ],
                    fields: [
                        { key: 'name', label: 'Name', required: true },
                        { key: 'designation', label: 'Designation' },
                        { key: 'dsc_id', label: 'Class III DSC ID' },
                        { key: 'poa_reference', label: 'Power of Attorney Reference' },
                        { key: 'phone', label: 'Phone Number' },
                        { key: 'email', label: 'Email Address' },
                    ],
                },
                contact: {
                    label: 'Contact Persons', singular: 'Contact', endpoint: 'contact-person', multipart: false,
                    columns: [
                        { key: 'name', label: 'Name' },
                        { key: 'designation', label: 'Designation' },
                        { key: 'telephone', label: 'Telephone' },
                        { key: 'email', label: 'Email' },
                    ],
                    fields: [
                        { key: 'name', label: 'Name', required: true },
                        { key: 'designation', label: 'Designation' },
                        { key: 'address', label: 'Address', type: 'textarea' },
                        { key: 'telephone', label: 'Telephone Number' },
                        { key: 'fax', label: 'Fax Number' },
                        { key: 'email', label: 'Email Address' },
                    ],
                },
                banker: {
                    label: 'Company Bankers', singular: 'Banker', endpoint: 'banker', multipart: false,
                    columns: [
                        { key: 'bank_name', label: 'Bank' },
                        { key: 'branch', label: 'Branch' },
                        { key: 'account_number', label: 'Account No' },
                        { key: 'ifsc_code', label: 'IFSC' },
                    ],
                    fields: [
                        { key: 'bank_name', label: 'Bank Name', required: true },
                        { key: 'branch', label: 'Branch' },
                        { key: 'account_number', label: 'Account Number' },
                        { key: 'ifsc_code', label: 'IFSC Code' },
                        { key: 'banker_address', label: "Banker's Address", type: 'textarea' },
                    ],
                },
            },
        };
    },
    computed: {
        isAdmin() {
            try {
                const u = JSON.parse(localStorage.getItem('user-info'));
                return u && u.roles && (u.roles.includes('admin') || u.roles.includes('superadmin'));
            } catch (e) { return false; }
        },
        currentConfig() {
            return this.configs[this.activeTab] || {};
        },
    },
    watch: {
        activeTab() { this.loadActive(); },
    },
    mounted() {
        this.loadActive();
    },
    methods: {
        icon(path) {
            return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;
        },
        token() {
            if (typeof this.getAccessToken === 'function') {
                const t = this.getAccessToken();
                if (t) return t;
            }
            try {
                const u = JSON.parse(localStorage.getItem('user-info'));
                return u && (u.access_token || u.token);
            } catch (e) { return ''; }
        },
        authHeaders(extra = {}) {
            return { Authorization: 'Bearer ' + this.token(), ...extra };
        },
        base() { return this.$API_URL + '/company-kb'; },

        // ---- Loading ----
        loadActive() {
            if (this.activeTab === 'profile') return this.loadProfile();
            return this.loadList();
        },
        async loadProfile() {
            this.loading = true;
            try {
                const res = await axios.get(this.base() + '/profile', { headers: this.authHeaders() });
                this.profile = res.data || null;
            } catch (e) { console.error(e); } finally { this.loading = false; }
        },
        async loadList() {
            this.loading = true;
            this.rows = [];
            try {
                const res = await axios.get(this.base() + '/' + this.currentConfig.endpoint, { headers: this.authHeaders() });
                this.rows = Array.isArray(res.data) ? res.data : [];
            } catch (e) { console.error(e); } finally { this.loading = false; }
        },

        // ---- Display helpers ----
        displayValue(val, f) {
            if (val === null || val === undefined || val === '') return '—';
            if (f && f.fmt === 'money') {
                const n = Number(val);
                if (!isFinite(n)) return val;
                return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
            }
            if (f && (f.fmt === 'date' || f.type === 'date')) {
                const d = new Date(val);
                if (isNaN(d.getTime())) return val;
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            }
            if (f && f.type === 'file') return val ? 'Uploaded' : '—';
            return val;
        },

        // ---- Modal open/close ----
        openProfileForm() {
            this.form = { ...(this.profile || {}) };
            this.uploads = {};
            this.errors = {};
            this.modal = { open: true, mode: this.profile ? 'edit' : 'create', title: 'Company Profile', fields: this.profileFields, rowId: null };
        },
        openCreate() {
            this.form = {};
            this.uploads = {};
            this.errors = {};
            this.modal = { open: true, mode: 'create', title: 'Add ' + this.currentConfig.singular, fields: this.currentConfig.fields, rowId: null };
        },
        openEdit(row) {
            this.form = { ...row };
            this.uploads = {};
            this.errors = {};
            this.modal = { open: true, mode: 'edit', title: 'Edit ' + this.currentConfig.singular, fields: this.currentConfig.fields, rowId: row.id };
        },
        openView(row) {
            this.form = { ...row };
            this.uploads = {};
            this.errors = {};
            this.modal = { open: true, mode: 'view', title: this.currentConfig.singular + ' Details', fields: this.currentConfig.fields, rowId: row.id };
        },
        closeModal() { this.modal.open = false; },
        clearError(key) {
            if (this.errors[key]) {
                // Vue 3 reactivity: reassign so the template updates.
                const next = { ...this.errors };
                delete next[key];
                this.errors = next;
            }
        },
        onFileChange(e, key) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            // Only PDF and JPG are allowed for certificate uploads.
            const name = (file.name || '').toLowerCase();
            const okExt = name.endsWith('.pdf') || name.endsWith('.jpg') || name.endsWith('.jpeg');
            const okType = ['application/pdf', 'image/jpeg'].includes(file.type);
            if (!okExt && !okType) {
                this.errors = { ...this.errors, [key]: 'Only PDF or JPG files are allowed.' };
                e.target.value = '';
                this.uploads[key] = null;
                return;
            }
            this.clearError(key);
            this.uploads[key] = file;
        },

        // ---- Save ----
        async save() {
            // Basic required validation — shown inline below each field (no alerts).
            const errs = {};
            for (const f of this.modal.fields) {
                const val = f.type === 'file' ? (this.uploads[f.key] || this.form[f.key]) : this.form[f.key];
                if (f.required && (val === null || val === undefined || String(val).trim() === '')) {
                    errs[f.key] = f.label + ' is required.';
                }
            }

            // Section-specific business rules (company-wise).
            if (this.activeTab === 'financial') {
                Object.assign(errs, this.validateFinancial());
            }

            this.errors = errs;
            if (Object.keys(errs).length) return;

            this.saving = true;
            try {
                if (this.activeTab === 'profile') {
                    await axios.post(this.base() + '/profile', this.profilePayload(), { headers: this.authHeaders() });
                    await this.loadProfile();
                } else if (this.currentConfig.multipart) {
                    await this.saveMultipart();
                    await this.loadList();
                } else {
                    await this.saveJson();
                    await this.loadList();
                }
                this.closeModal();
            } catch (e) {
                // Surface a backend validation message inline rather than as an alert.
                this.applyServerError(e);
                console.error(e);
            } finally {
                this.saving = false;
            }
        },
        validateFinancial() {
            const errs = {};
            const year = this.form.finance_year;

            // Unique finance year per company — must edit, not re-add.
            if (year) {
                const dup = this.rows.some(r => r.finance_year === year && r.id !== this.modal.rowId);
                if (dup) {
                    errs.finance_year = `Financial year ${year} already exists. Please edit the existing entry.`;
                }
            }

            // Turnover must be greater than net profit after tax.
            const turnover = this.form.total_turnover;
            const netProfit = this.form.net_profit_after_tax;
            const hasT = turnover !== null && turnover !== undefined && String(turnover).trim() !== '';
            const hasN = netProfit !== null && netProfit !== undefined && String(netProfit).trim() !== '';
            if (hasT && hasN && Number(turnover) <= Number(netProfit)) {
                errs.total_turnover = 'Total Turnover must be greater than Net Profit After Tax.';
            }
            return errs;
        },
        applyServerError(e) {
            const msg = e && e.response && e.response.data &&
                (e.response.data.message || e.response.data.detail);
            if (!msg) return;
            const lower = String(msg).toLowerCase();
            // Attach the backend message to the most relevant field when we can.
            if (lower.includes('financial year') || lower.includes('finance year')) {
                this.errors = { ...this.errors, finance_year: msg };
            } else if (lower.includes('turnover')) {
                this.errors = { ...this.errors, total_turnover: msg };
            } else {
                // Fallback: show against the first field so it's still inline.
                const first = (this.modal.fields[0] || {}).key;
                if (first) this.errors = { ...this.errors, [first]: msg };
            }
        },
        profilePayload() {
            const p = {};
            for (const f of this.profileFields) p[f.key] = this.form[f.key] ?? null;
            return p;
        },
        jsonPayload() {
            const p = {};
            for (const f of this.currentConfig.fields) {
                if (f.type === 'file') continue;
                p[f.key] = this.form[f.key] ?? null;
            }
            return p;
        },
        async saveJson() {
            const url = this.base() + '/' + this.currentConfig.endpoint + (this.modal.mode === 'edit' ? '/' + this.modal.rowId : '');
            const method = this.modal.mode === 'edit' ? 'put' : 'post';
            // suppressErrorAlert: backend validation messages are rendered inline.
            await axios[method](url, this.jsonPayload(), { headers: this.authHeaders(), suppressErrorAlert: true });
        },
        async saveMultipart() {
            const fd = new FormData();
            for (const f of this.currentConfig.fields) {
                if (f.type === 'file') {
                    if (this.uploads[f.key]) fd.append(f.upload, this.uploads[f.key]);
                } else if (this.form[f.key] !== null && this.form[f.key] !== undefined && this.form[f.key] !== '') {
                    fd.append(f.key, this.form[f.key]);
                }
            }
            const url = this.base() + '/' + this.currentConfig.endpoint + (this.modal.mode === 'edit' ? '/' + this.modal.rowId : '');
            const method = this.modal.mode === 'edit' ? 'put' : 'post';
            await axios[method](url, fd, { headers: this.authHeaders({ 'Content-Type': 'multipart/form-data' }) });
        },

        // ---- Delete ----
        async confirmDelete(row) {
            const name = this.currentConfig.singular || 'Record';
            const ok = await confirmAction({
                title: `Delete ${name}?`,
                message: 'This record and any attached file are removed from the knowledge base. This can’t be undone.',
                confirmLabel: `Delete ${name}`,
                destructive: true,
            });
            if (!ok) return;
            try {
                await axios.delete(this.base() + '/' + this.currentConfig.endpoint + '/' + row.id, { headers: this.authHeaders() });
                await this.loadList();
                notify.success(`${name} deleted`);
            } catch (e) {
                console.error(e);
                notify.error(`Couldn’t delete the ${name.toLowerCase()}. Check your connection and try again.`);
            }
        },

        // ---- File download ----
        async downloadFile(rowId, kind) {
            // kind: 'certificate' | 'invoice' | 'report'
            const map = {
                certificate: 'work-experience/' + rowId + '/certificate',
                invoice: 'software/' + rowId + '/invoice',
                report: 'audit/' + rowId + '/report',
                cv: 'manpower/' + rowId + '/cv',
            };
            try {
                const res = await axios.get(this.base() + '/' + map[kind], {
                    responseType: 'blob', headers: this.authHeaders(),
                });
                let filename = kind + '-' + rowId;
                const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
                if (disp) {
                    const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
                    if (m && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
                }
                const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 100);
            } catch (e) {
                console.error('Download failed', e);
                notify.error('Couldn’t download the file. Check your connection and try again.');
            }
        },
    },
};
</script>

<style scoped>
.ckb-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);
}

/* Header */
.ckb-header {
    background: linear-gradient(135deg, #001F3F 0%, #003D7A 60%, #001F3F 100%);
    color: #fff;
    border-radius: 20px;
    padding: 1.6rem 2rem;
    margin-bottom: 1.5rem;
    position: relative;
    overflow: hidden;
}

.ckb-header::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.4;
}

.ckb-header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 2;
}

.ckb-header-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.16);
    flex-shrink: 0;
}

.ckb-title {
    font-family: var(--font-display);   /* Outfit — page title */
    margin: 0;
    font-size: var(--fs-h1);            /* 36px page title */
    line-height: var(--lh-h1);          /* 44px */
    font-weight: var(--fw-h1);          /* 700 */
    letter-spacing: -0.02em;
    /* White on the navy header — matches the BidDetails hero title. Needed to
       beat the global `h1 { color: var(--text-primary) }` (dark ink) rule. */
    color: #fff !important;
}

.ckb-subtitle {
    margin: 0.3rem 0 0;
    font-size: var(--fs-sm);            /* 13px small */
    line-height: var(--lh-sm);          /* 20px */
    font-weight: var(--fw-sm);          /* 500 */
    /* White (dimmed) on the navy header — matches BidDetails' .bd-subname,
       replacing the dark --text-secondary that was unreadable here. */
    color: rgba(255, 255, 255, 0.8);
    text-align: left;
}

/* Tabs */
.ckb-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
}

.ckb-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.6rem 1rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    font-weight: 700;
    font-size: 0.82rem;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.ckb-tab:hover {
    border-color: #0071C5;
    color: #0071C5;
}

.ckb-tab.active {
    background: #0071C5;
    color: #fff;
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(0, 113, 197, 0.25);
}

/* Panel */
.ckb-panel {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 20px;
    padding: 1.5rem 1.75rem;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.panel-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
}

.panel-head h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: #0f172a;
}

/* Buttons */
.ckb-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.55rem 1rem;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.82rem;
    cursor: pointer;
    border: 1px solid transparent;
    font-family: var(--font-sans);
    transition: all 0.2s;
}

.ckb-btn.primary {
    background: #0071C5;
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 113, 197, 0.25);
}

.ckb-btn.primary:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
}

.ckb-btn.ghost {
    background: #f1f5f9;
    color: #334155;
}

.ckb-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* States */
.ckb-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 3rem;
    color: #64748b;
    font-weight: 600;
}

.ckb-state.empty {
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
}

.ckb-spinner {
    width: 26px;
    height: 26px;
    border: 3px solid #e2e8f0;
    border-top-color: #0071C5;
    border-radius: 50%;
    animation: ckb-spin 1s linear infinite;
}

@keyframes ckb-spin {
    to { transform: rotate(360deg); }
}

/* Profile view */
.profile-view {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem 2rem;
}

.profile-field {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0;
    border-bottom: 1px dashed #eef2f7;
}

.pf-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: #64748b;
}

.pf-value {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1e293b;
    text-align: right;
}

/* Table */
.ckb-table-wrap {
    overflow-x: auto;
}

.ckb-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
}

.ckb-table th {
    text-align: left;
    vertical-align: middle;
    padding: 0.75rem 0.85rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #475569;
    background: #f1f5f9;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
}

.ckb-table td {
    /* Explicit so cells stay locked to the left-aligned th above. */
    text-align: left;
    padding: 0.75rem 0.85rem;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
}

.ckb-table tbody tr:nth-child(even) td {
    background: #fafbfc;
}

.ckb-table tbody tr:hover td {
    background: #eff6ff;
}

.ckb-table a {
    color: #0071C5;
    font-weight: 700;
    text-decoration: none;
}

.col-actions {
    text-align: right;
    white-space: nowrap;
}

.icon-btn {
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 8px;
    padding: 0.35rem;
    margin-left: 0.3rem;
    cursor: pointer;
    color: #64748b;
    transition: all 0.2s;
    vertical-align: middle;
}

.icon-btn.view:hover { color: #0071C5; border-color: #0071C5; }
.icon-btn.edit:hover { color: #f59e0b; border-color: #f59e0b; }
.icon-btn.delete:hover { color: #f43f5e; border-color: #f43f5e; }

/* Modal */
.ckb-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.ckb-modal {
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
    font-family: var(--font-sans);
}

.ckb-modal-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #eef2f7;
}

.ckb-modal-head h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
}

.ckb-modal-close {
    border: none;
    background: none;
    font-size: 1.6rem;
    line-height: 1;
    color: #64748b;
    cursor: pointer;
}

.ckb-modal-body {
    padding: 1.25rem 1.5rem;
    overflow-y: auto;
}

.ckb-form-row {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.ckb-form-row label {
    flex: 0 0 40%;
    max-width: 40%;
    font-size: 0.8rem;
    font-weight: 700;
    color: #475569;
    margin-bottom: 0;
    text-align: left;
    padding-top: 0.65rem;
    /* visually align label baseline with single-line input text */
}

/* The control side (input/select/textarea/file/static) takes the rest of the row */
.ckb-form-row>.ckb-input,
.ckb-form-row>.form-static,
.ckb-form-row>.ckb-control,
.ckb-form-row>div {
    flex: 1 1 auto;
    min-width: 0;
}

/* Control column wraps the input plus its hint / inline error */
.ckb-control {
    display: flex;
    flex-direction: column;
}

.ckb-input.has-error {
    border-color: #f43f5e;
    background: #fff5f6;
}

.ckb-input.has-error:focus {
    box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.12);
}

.field-error {
    margin-top: 0.3rem;
    font-size: 0.74rem;
    font-weight: 600;
    color: #e11d48;
    text-align: left;
}

.req {
    color: #f43f5e;
    margin-left: 2px;
}

.ckb-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 0.6rem 0.75rem;
    font-size: 0.88rem;
    font-family: var(--font-sans);
    color: #1e293b;
    background: #f8fafc;
    box-sizing: border-box;
}

.ckb-input:focus {
    outline: none;
    border-color: #0071C5;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.1);
}

.form-static {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e293b;
    padding: 0.3rem 0;
}

.form-hint {
    font-size: 0.72rem;
    color: #64748b;
    margin-top: 0.3rem;
}

.ckb-modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid #eef2f7;
}

@media (max-width: 700px) {
    .profile-view {
        grid-template-columns: 1fr;
    }
}
</style>
